const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const TransactionResponseRecord = require('./Records/Response/WorldpayTransactionResponseRecord');

/**
 * See the Worldpay integration guide "IPC-2 PA-DSS-2.1.9.pdf" for more background on how the Worldpay
 * integration works.
 * In short:
 * - Worldpay's IPC application runs on Windows
 * - ProPoint communicates with it via a socket
 *   (via the "socket-proxy" app: https://bitbucket.org/digitickets/socket-proxy)
 * - Messages are sent/received as a bunch of lines in the format 1=234, where 1 is the field identifier
 *   and 234 is its value. Every message ends with a line "99=0" to signify the end of that message.
 *
 * Guide is at:
 * https://drive.google.com/file/d/1dZARnCoE793lByj2l8me7XVk-Um1EDpN/view?usp=sharing
 *
 * @constructor
 *
 * @param $interval
 * @param {AgentService} agentService
 * @param {DeviceManager} deviceManager
 * @param {Hydrator} hydrator
 * @param {ReceiptPrinter} receiptPrinter
 * @param {ToastFactory} toastFactory
 * @param {WorldpayConfig} worldpayConfigService
 */
const WorldpayDriver = function (
    $interval,
    agentService,
    deviceManager,
    hydrator,
    receiptPrinter,
    toastFactory,
    worldpayConfigService
) {
    this.$interval = $interval;
    this.agentService = agentService;
    this.config = worldpayConfigService;
    this.deviceManager = deviceManager;
    this.hydrator = hydrator;
    this.receiptPrinter = receiptPrinter;
    this.toastFactory = toastFactory;

    this.log = logFactory('Worldpay');

    /**
     * Callbacks to be fired when a message is received from the terminal.
     *
     * @type {function[]}
     */
    this.recordReceivedListeners = [];

    /**
     * @type {TcpSocketConnection}
     */
    this.socket = null;

    /**
     * @type {TcpSocketConnection}
     */
    this.receiptSocket = null;

    /**
     * Keep track of the state of the terminal.
     */
    this.state = {
        /**
         * @type {WorldpayTransactionResponseRecord|null}
         */
        lastRecordReceived: null,
    };

    /**
     * @type {Array}
     */
    this.queuedRecords = [];

    let configValid = false;
    try {
        configValid = this.config.validate();
    } catch (e) {
        this.toastFactory.errorTop(
            'WORLDPAY_SETTINGS.CONFIG_ERROR',
            e.message
        );
    }

    if (configValid) {
        this.init();
    }
};

WorldpayDriver.prototype = {
    init: function () {
        this.agentService.onError = () => {
            this.toastFactory.error(
                null,
                'There was a problem connecting to ProPoint Agent.'
            );
        };

        this.connect().then(() => {
            this.$interval(() => {
                this.sendNextQueuedRecord();
            }, 500);
        });
    },

    /**
     * @returns {Promise}
     */
    connect() {
        return Promise.all(
            [
                this.connectSocket(),
                this.connectReceiptSocket(),
            ]
        );
    },

    /**
     * @private
     * @returns {Promise}
     */
    connectSocket() {
        // Don't reconnect if already connected.
        if (this.socket) {
            this.log.debug('Socket already connected.');
            return Promise.resolve();
        }

        this.log.debug('Connecting socket.');
        this.socket = this.createTcpSocket(
            'Worldpay Socket',
            this.config.terminalHost,
            this.config.terminalPort
        );

        return this.socket.connect()
            .then(() => {
                this.log.debug('Socket connected.');
            });
    },

    /**
     * @private
     * @returns {Promise}
     */
    connectReceiptSocket() {
        if (!this.config.printReceipts) {
            return Promise.resolve();
        }

        // Disconnect any existing connection first.
        let disconnectPromise;

        if (this.receiptSocket) {
            this.log.debug('Disconnecting existing receipt socket.');
            disconnectPromise = this.receiptSocket.disconnect()
                .then(() => {
                    this.log.debug('Receipt socket disconnected.');
                });
        } else {
            disconnectPromise = Promise.resolve();
        }

        return disconnectPromise.then(() => {
            this.log.debug('Connecting receipt socket.');

            this.receiptSocket = this.createReceiptSocket(
                'Worldpay Receipt Socket',
                this.config.terminalHost,
                this.config.receiptPort
            );
            return this.receiptSocket.connect()
                .then(() => {
                    this.log.debug('Receipt socket connected.');
                });
        });
    },

    /**
     * @param {WorldpayTransactionRequestRecord} record
     */
    sendRecord: function sendRecord(record) {
        if (this.queuedRecords.length > 0) {
            let lastRecord = this.queuedRecords[this.queuedRecords.length - 1];
            let lastRecordString = this.recordToString(lastRecord);
            if (lastRecordString === this.recordToString(record)) {
                this.log.debug('Skipping duplicate record', lastRecordString);
                return;
            }
        }

        this.queuedRecords.push(record);
    },

    /**
     * @param {WorldpayTransactionRequestRecord} record
     */
    sendRecordNow: function sendRecordNow(record) {
        let dataString = this.recordToString(record);
        this.log.debug('Sending', JSON.stringify(dataString));
        this.socket.sendData(dataString);
    },

    /**
     * To avoid sending records to IPC too quickly, instead of sending them when requested they go into a queue.
     * The next record from the queue is sent in 1 second intervals.
     */
    sendNextQueuedRecord: function sendNextQueuedRecord() {
        let nextRecord = this.queuedRecords.shift();
        if (nextRecord) {
            this.sendRecordNow(nextRecord);
        }
    },

    /**
     * Convert the record object to a string of data to send on the socket.
     *
     * Messages are sent over the socket as one value per line.
     * For example:
     * 1=abc
     * 2=def
     * 70=ghi
     * 99=0
     *
     * Every message ends with 99=0 to signify the end of the message.
     * See the Worldpay integration PDF for more info.
     *
     * @param {WorldpayTransactionRequestRecord} record
     *
     * @return {string}
     */
    recordToString: function recordToString(record) {
        let str = '';
        let data = record.toData();
        for (let key in data) {
            if (data.hasOwnProperty(key) && data[key] !== null) {
                str += key + '=' + data[key] + '\n';
            }
        }
        str += '99=0';

        return str;
    },

    /**
     * Create a new TCP socket object via the WebSocket connection.
     * Setup listeners to handle data and errors.
     *
     * @private
     * @param {string} name
     * @param {string} host
     * @param {number} port
     *
     * @return {TcpSocketConnection}
     */
    createTcpSocket: function (name, host, port) {
        let tcpSocket = this.agentService.createTcpSocket(
            name,
            host,
            port
        );

        tcpSocket.onError = (errorString) => {
            this.toastFactory.error(
                'PAYMENT.TERMINAL_ERROR',
                `There was a problem connecting to the Worldpay terminal.<br/>${errorString}`
            );
        };

        // Add listener for data coming from the TCP socket.
        tcpSocket.onData = (dataString, socket) => {
            this.log.debug('Received', JSON.stringify(dataString));
            let record = this.createRecordFromSocketData(dataString, socket);
            if (record) {
                this.handleReceivedRecord(record, socket);
            }
        };

        return tcpSocket;
    },

    /**
     * @private
     * @param name
     * @param host
     * @param port
     * @returns {TcpSocketConnection}
     */
    createReceiptSocket: function (name, host, port) {
        const tcpSocket = this.agentService.createTcpSocket(
            name,
            host,
            port
        );

        tcpSocket.onError = (errorString) => {
            this.toastFactory.error(
                'PAYMENT.TERMINAL_ERROR',
                `There was a problem connecting to the Worldpay receipt socket.<br/>${errorString}`
            );
        };

        // Add listener for data coming from the TCP socket.
        tcpSocket.onData = (dataString) => {
            // Receipts issued on the Receipt Socket port are prefixed by labels CUSTOMER: and MERCHANT: so
            // that their start point may be readily identified in the stream of data from the socket.
            // These label should removed from the receipt data and not printed.
            dataString = dataString.replace(/^(CUSTOMER|MERCHANT):/, '');

            // Replace newlines with <br/> so it can be printed as HTML.
            dataString = dataString.replace(/\n/g, '<br/>');

            this.log.log('Printing receipt', dataString);

            this.receiptPrinter.printWorldpayCardReceipt(dataString);
        };

        return tcpSocket;
    },

    /**
     * Receives the raw string data from a socket and creates a record object from it.
     *
     * @param {string} dataString
     *
     * @return {WorldpayTransactionResponseRecord}
     */
    createRecordFromSocketData: function createRecordFromSocketData(dataString) {
        // Split into separate lines
        let dataLines = dataString.split('\n');

        let data = {
            rawResponse: dataString,
        };

        for (let i = 0; i < dataLines.length; i++) {
            let line = dataLines[i];
            // Convert from the attribute=value format
            let equalsPos = line.indexOf('=');
            if (!equalsPos) {
                continue;
            }
            let attribute = line.substr(0, equalsPos);
            data[attribute] = line.substr(equalsPos + 1);
        }

        return this.hydrator.hydrate(
            data,
            new TransactionResponseRecord()
        );
    },

    /**
     * Do something with a record received from the terminal.
     * Then pass it on to any listeners.
     *
     * @param {WorldpayTransactionResponseRecord} record
     * @param {TcpSocketConnection} socket
     */
    handleReceivedRecord: function handleReceivedRecord(record, socket) {
        this.setLastReceivedRecord(record);

        // Tell other places that may be interested in this record (the payment controller).
        this._notifyResponseListeners(record, socket);
    },

    /**
     * Set the last record that was received from the terminal on that socket.
     *
     * @param {object} record
     */
    setLastReceivedRecord: function setLastReceivedRecord(record) {
        this.state.lastRecordReceived = record;
    },

    /**
     * Register a new listener that will be fired when a record is received from the terminal.
     *
     * @param {function} listener
     */
    onRecordReceived: function onRecordReceived(listener) {
        this.recordReceivedListeners.push(listener);
    },

    /**
     * Un-register all record received listeners.
     */
    clearRecordReceivedListeners: function clearRecordReceivedListeners() {
        this.recordReceivedListeners = [];
    },

    /**
     * Notify all the registered response listeners.
     *
     * @param {object} record
     * @param {TcpSocketConnection} socket
     *
     * @private
     */
    _notifyResponseListeners: function _notifyResponseListeners(record, socket) {
        for (let r = 0; r < this.recordReceivedListeners.length; r++) {
            this.recordReceivedListeners[r](record, socket);
        }
    },
};

module.exports = WorldpayDriver;
